import { alpha } from '@mui/material';

export const getExchangeSubscriptionsStyles = (theme, isDark) => ({
  card: {
    mb: 4,
    borderRadius: { xs: 2, sm: 3 },
    overflow: 'hidden',
    border: `1px solid ${alpha(theme.palette.text.primary, 0.2)}`,
    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
    transition: 'all 0.2s ease',
  },
  header: {
    px: { xs: 1.5, sm: 2 },
    py: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: isDark
      ? `linear-gradient(45deg, ${alpha(theme.palette.grey[900], 0.9)}, ${alpha(theme.palette.grey[800], 0.9)})`
      : `linear-gradient(45deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  headerIcon: {
    fontSize: 18,
    color: theme.palette.primary.main,
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: { xs: '0.95rem', sm: '1.05rem' },
  },
  summaryChip: {
    px: 1,
    py: 0.15,
    borderRadius: 1,
    bgcolor: alpha(theme.palette.primary.main, 0.08),
    display: 'flex',
    alignItems: 'center',
  },
  summaryText: {
    fontWeight: 700,
    color: theme.palette.primary.main,
    fontSize: '0.65rem',
  },
  columnHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    px: { xs: 1.5, sm: 2 },
    py: 0.75,
    borderBottom: `1px solid ${theme.palette.divider}`,
    bgcolor: isDark ? alpha(theme.palette.background.paper, 0.4) : alpha(theme.palette.grey[50], 0.5),
  },
  columnHeaderText: {
    fontSize: '0.7rem',
    fontWeight: 800,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  mobileExchangeHeader: {
    display: { xs: 'block', sm: 'none' },
  },
  desktopHeadersLeft: {
    display: { xs: 'none', sm: 'flex' },
    gap: 3, // Match gap from desktopLeftSection
  },
  columnHeaderLabelBox: {
    width: 65,
    display: 'flex',
    justifyContent: 'center',
  },
  columnHeaderRight: {
    display: 'flex',
    gap: 2, 
  },
  rowsContainer: {
    p: { xs: 0, sm: 0 },
  },
});

export const getExchangeRowStyles = (theme, isDark, isEnabled, isLast) => ({
  row: {
    display: 'block',
    width: '100%',
    px: { xs: 1.5, sm: 2 },
    py: { xs: 0.75, sm: 0.85 }, // More comfortable padding
    borderBottom: isLast ? 'none' : `1px solid ${theme.palette.divider}`,
    transition: 'background-color 0.15s ease',
    '&:hover': {
      bgcolor: isDark
        ? alpha(theme.palette.grey[800], 0.4)
        : alpha(theme.palette.grey[100], 0.6),
    },
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  enabledIcon: {
    fontSize: 15,
    color: '#22c55e',
  },
  disabledIcon: {
    fontSize: 15,
    color: isDark ? theme.palette.grey[500] : theme.palette.grey[400],
  },
  exchangeName: {
    fontWeight: 700,
    fontSize: { xs: '0.85rem', sm: '0.9rem' },
    color: isEnabled
      ? theme.palette.text.primary
      : theme.palette.text.disabled,
    letterSpacing: '0.3px',
    lineHeight: 1.1,
  },
  textStack: {
    display: { xs: 'flex', sm: 'none' }, // Only stack on mobile
    flexDirection: 'column',
    justifyContent: 'center',
  },
  desktopLeftSection: {
    display: { xs: 'none', sm: 'flex' },
    alignItems: 'center',
    gap: 3, // gap between exchange, time, days
  },
  desktopColumn: {
    display: 'flex',
    alignItems: 'center',
  },
  timingText: {
    fontSize: { xs: '0.8rem', sm: '0.85rem' },
    color: theme.palette.text.primary,
    fontWeight: 500,
    lineHeight: 1.1,
  },
  daysText: {
    fontSize: { xs: '0.8rem', sm: '0.85rem' },
    color: { xs: theme.palette.text.disabled, sm: theme.palette.text.secondary },
    fontWeight: 600,
    lineHeight: 1.1,
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  badgeContainer: {
    width: 65,
    display: 'flex',
    justifyContent: 'center',
  },
  badge: {
    px: 0.6,
    py: 0.05,
    borderRadius: 1, // 8px for a nice rounded rectangle pill
    bgcolor: isEnabled
      ? alpha('#22c55e', isDark ? 0.15 : 0.1)
      : alpha(theme.palette.error.main, isDark ? 0.12 : 0.06),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 50, // slightly larger for consistency
  },
  badgeText: {
    fontSize: '0.7rem', // Better readability
    fontWeight: 800,
    color: isEnabled ? '#22c55e' : theme.palette.error.main,
    textTransform: 'uppercase',
    letterSpacing: '0.2px',
  },
  marketBadge: (isOpen) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.4,
    px: 0.4,
    py: 0.05,
    borderRadius: 1, // 8px for rounded rectangle
    minWidth: 44, // reduced for tighter fit
    bgcolor: isOpen
      ? alpha('#22c55e', isDark ? 0.15 : 0.1)
      : alpha(theme.palette.error.main, isDark ? 0.12 : 0.06),
  }),
  marketBadgeText: (isOpen) => ({
    fontSize: '0.7rem', // Better readability
    fontWeight: 800,
    color: isOpen ? '#22c55e' : theme.palette.error.main,
    letterSpacing: '0.2px',
  }),
  dot: (isOpen) => ({
    width: 4,
    height: 4,
    borderRadius: '50%',
    bgcolor: isOpen ? '#22c55e' : theme.palette.error.main,
    animation: isOpen ? 'pulse 2s infinite' : 'none',
    '@keyframes pulse': {
      '0%': { boxShadow: '0 0 0 0 rgba(34, 197, 94, 0.4)' },
      '70%': { boxShadow: '0 0 0 3px rgba(34, 197, 94, 0)' },
      '100%': { boxShadow: '0 0 0 0 rgba(34, 197, 94, 0)' },
    },
  }),
});
